import type { MeResponse } from '@palaccount/contracts';
import { X } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { Sidebar } from '@/app/shell/sidebar';
import { TopbarQuickLinks } from '@/app/shell/topbar';

/**
 * Anything inside the panel a keyboard can land on. Disabled controls are
 * filtered at the call site rather than here, because `:not([disabled])` says
 * nothing about `aria-disabled`, and a menu that grows one later should not
 * silently start trapping focus on a dead control.
 */
const FOCUSABLE =
  'a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])';

/**
 * THE §4.3.1 MENU COLUMN, ON A NARROW WINDOW — D25 (delegated ruling,
 * 26 Aug 2026): "narrow screens → slide-over menu behind a button. Desktop
 * unchanged."
 *
 * ── IT IS THE SAME MENU, NOT A SECOND ONE ─────────────────────────────────
 *
 * This component draws `<Sidebar>` — the identical component the wide frame
 * mounts — and adds a scrim, a close button and the modal behaviour around it.
 * Nothing about the menu's content, order, permissions or Arabic labels is
 * restated here, which is the point: `visibleGroups` (BR-PERM-19, §1.5.3)
 * cannot come to two different answers on two window widths, because there is
 * only one implementation to ask. A narrow build that reimplemented the list
 * would be a second place for a permission bug to live.
 *
 * ── RETIRED BY DELEGATED RULING: GEN-1's SECOND CLAUSE, AND §4.3.1's PROSE ──
 *
 * GEN-1 (spec line 3311) — "The side menu must be 256 pixels wide and must be
 * present on every screen after sign-in" — and §4.3.1's opening sentence
 * (spec line 3268) — "It never gets narrower and never disappears" — are both
 * true of the wide frame and deliberately not of the narrow one. Retired by
 * delegated ruling D25, which is REVERSIBLE: the owner may overrule it, and if
 * they do, the whole change is the conditional in `app-shell.tsx`.
 *
 * What survives here is the WIDTH: the panel is `w-64`, so the menu a narrow
 * reader opens is a full 256px column with every label showing. The desktop
 * frame's menu is a hover-expanding RAIL since the owner's Oct 2026 ruling
 * (`sidebar.tsx`); this panel mounts the same component as `variant="panel"`
 * — always open, no pin, no bubbles — so nothing about D25 changed with it.
 *
 * ── WHY THIS IS NOT `<dialog>`, WHEN `confirm-box.tsx` IS ─────────────────
 *
 * The shared confirmation box uses a native `<dialog>` with `showModal()`,
 * and its own comment records why that is right there: §8 demands it sit above
 * another dialog, which is the browser's top layer. It also gets Escape and a
 * focus trap for free — in a BROWSER. jsdom implements `<dialog>` without
 * `showModal`, which `confirm-box.tsx` documents and works around by falling
 * back to the `open` attribute; that fallback keeps the markup and loses
 * exactly the modal behaviour. For a confirmation box that is a fair trade,
 * because no test there measures the trap.
 *
 * Here the trap, the Escape key and where focus goes ARE the behaviour under
 * test. Written as explicit handlers they behave the same in every engine and,
 * more to the point, each one can be broken on its own to prove the test that
 * covers it actually fires. A behaviour delegated to the platform is a
 * behaviour whose test cannot be made to fail honestly.
 *
 * ── THE RULES THIS ENFORCES, BY ID ────────────────────────────────────────
 *
 *   GEN-182 / NFR-37  the close button is icon-only, so it carries a text
 *     label for screen readers AND a tooltip for the pointer. Its opener in
 *     `topbar.tsx` carries the same pair.
 *   GEN-184 / NFR-38  usable with the keyboard alone, in reading order, with
 *     the focused control visible: focus enters the panel when it opens,
 *     cycles inside it while it is open, and returns to the control that
 *     opened it when it closes.
 *   NFR-02  every offset here is logical (`start-0`, `end-2`, `inset-y-0`),
 *     never left/right, so the panel comes from the leading edge — the left in
 *     English, the RIGHT in Arabic — with no second stylesheet and no
 *     direction prop. `d25-narrow-nav.test.tsx` asserts the absence of every
 *     physical-direction utility across the four shell files rather than
 *     trusting this sentence.
 *
 * GEN-187 is NOT cited here, and `wide-content-scrolls.test.tsx` carries the  // rule-coverage:ignore
 * argument in full: it is a per-SCREEN rule with three clauses, this is the
 * frame, and closing the frame's share of one clause is not enforcing it.
 */
export function NavSlideOver({ me, onClose }: { me: MeResponse; onClose: () => void }) {
  const { t } = useTranslation();
  const panelRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  /*
   * FOCUS IN, AND BACK OUT AGAIN.
   *
   * The element that had focus when the panel mounted is remembered and given
   * it back on unmount, rather than the shell passing a ref to the button. It
   * is the same result for the one caller that exists and it survives a second
   * one: whatever opened the menu is what gets focus back, including a control
   * that has not been written yet. A `?.` on the restore because the opener
   * can legitimately be gone — widening the window unmounts both the panel and
   * the button in the same commit, and focusing a detached node is a no-op
   * rather than a mistake to guard against.
   */
  useEffect(() => {
    const opener = document.activeElement;
    closeRef.current?.focus();
    return () => {
      if (opener instanceof HTMLElement) opener.focus();
    };
  }, []);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== 'Tab') return;

      const panel = panelRef.current;
      if (panel === null) return;
      const focusable = [...panel.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
        (element) => !element.hasAttribute('disabled') && element.getAttribute('aria-hidden') !== 'true',
      );
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (first === undefined || last === undefined) return;

      // Off the end in either direction — or focus somehow outside the panel
      // entirely — comes back to the other end. GEN-184: a menu that a Tab can
      // walk out of, leaving a scrim over the page and no way back, is not
      // usable with the keyboard alone.
      const active = document.activeElement;
      const outside = !(active instanceof Node) || !panel.contains(active);
      if (event.shiftKey ? active === first || outside : active === last || outside) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      }
    }

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50">
      {/*
        The scrim. A <div> and not a <button>: it has no label worth speaking,
        and putting an unnamed control in the tab order ahead of the menu would
        make the keyboard path worse in order to satisfy a rule about pointers.
        Escape and the close button are the keyboard's two ways out, both
        tested, so nothing here is reachable only by mouse (GEN-181/NFR-36 are
        about controls; this is a backdrop, exactly as `confirm-box.tsx`'s own
        backdrop-click is).
      */}
      <div
        data-testid="nav-scrim"
        aria-hidden
        onClick={onClose}
        className="absolute inset-0 bg-black/50"
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={t('shell.mainMenu')}
        // `start-0`, not `left-0`: the menu comes from the leading edge, which
        // is the right-hand side under dir="rtl" (NFR-02). `max-w-[85vw]`
        // leaves the scrim reachable by pointer on a 320px window, where a
        // full 256px panel would otherwise leave 64px of page to tap.
        className="absolute inset-y-0 start-0 flex w-64 max-w-[85vw] flex-col shadow-xl"
      >
        <div className="min-h-0 flex-1 overflow-hidden">
          <Sidebar me={me} variant="panel" />
        </div>
        {/* UB-03 — the top bar's four quick links live HERE on the narrow
            frame, where the bar has no room for them. Outside the <nav>, so
            the menu stays exactly the thirteen groups. */}
        <div className="flex shrink-0 items-center gap-1 border-e border-t border-border bg-card px-2 py-1.5">
          <TopbarQuickLinks />
        </div>
        <button
          ref={closeRef}
          type="button"
          onClick={onClose}
          // GEN-182 / NFR-37: the name for a screen reader and the tooltip for
          // a pointer, from one dictionary key, exactly as the top bar's
          // icon-only controls do it.
          aria-label={t('shell.closeMenu')}
          title={t('shell.closeMenu')}
          className="absolute end-2 top-3 flex size-9 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
        >
          <X className="size-4.5" aria-hidden />
        </button>
      </div>
    </div>
  );
}
